import { MapUtils } from '../../../utility/map-utils.js';
import {
  DataModel,
  DataModelBuilder,
  SerializationContext,
  SerializedData,
  isSameData,
  isSameList,
} from '../../dto/dto.js';
import { LocalizedString } from '../localized-string.js';
import { PhotoReference } from '../file-reference/photo-reference.js';
import { Price } from '../price.js';
import { IDataWithLocalizedName, IDataWithLocalizedDescription } from '../../../contracts/localized-data.js';
import { VersionRange } from '../../core/version-range.js';
import { PortionUnit } from './portion-unit.js';
import { Nutrients } from './nutrients.js';
import { Allergens } from './allergens.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';

export class MenuItem extends DataModel implements IDataWithLocalizedName, IDataWithLocalizedDescription {
  static readonly TYPE_ID: string = 'b52a41fe0be44570a03cebe929ab394c';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
    public readonly uniqueId: string,
    public readonly names: LocalizedString,
    public readonly descriptions: LocalizedString,
    public readonly photo: PhotoReference | null,
    public readonly price: Price,
    public readonly portionSize: number,
    public readonly portionUnit: PortionUnit,
    public readonly allergens: Allergens[],
    public readonly ingredients: LocalizedString | null,
    public readonly nutrients: Nutrients | null,
  ) {
    super();
  }

  get $typeId(): string {
    return MenuItem.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, MenuItem.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): MenuItem {
    return new MenuItem(
        MapUtils.get<string>(map, 'uniqueId'),
        context.fromData<LocalizedString>(MapUtils.get<SerializedData>(map, 'names')),
        context.fromData<LocalizedString>(MapUtils.get<SerializedData>(map, 'descriptions')),
        context.fromDataOrNull<PhotoReference>(MapUtils.getOrNull<SerializedData>(map, 'photo')),
        Price.decode(MapUtils.get<string>(map, 'price')),
        MapUtils.get<number>(map, 'portionSize'),
        toEnumFromString(MapUtils.get<string>(map, 'portionUnit'), PortionUnit),
        MapUtils.getList<Allergens>(map, 'allergens', (value) => toEnumFromString(value as string, Allergens)),
        context.fromDataOrNull<LocalizedString>(MapUtils.getOrNull<SerializedData>(map, 'ingredients')),
        context.fromDataOrNull<Nutrients>(MapUtils.getOrNull<SerializedData>(map, 'nutrients')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'names': context.toData(this.names),
      'descriptions': context.toData(this.descriptions),
      'photo': context.toDataOrNull(this.photo),
      'price': this.price.encode(),
      'portionSize': this.portionSize,
      'portionUnit': fromEnumAsString(this.portionUnit),
      'allergens': this.allergens.map((e) => fromEnumAsString(e)),
      'ingredients': context.toDataOrNull(this.ingredients),
      'nutrients': context.toDataOrNull(this.nutrients),
    };
  }

  clone(): this {
    return new MenuItem(
        this.uniqueId,
        this.names.clone(),
        this.descriptions.clone(),
        this.photo?.clone() ?? null,
        this.price.clone(),
        this.portionSize,
        this.portionUnit,
        this.allergens,
        this.ingredients?.clone() ?? null,
        this.nutrients?.clone() ?? null,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId == this.uniqueId &&
       isSameData(other.names, this.names) &&
       isSameData(other.descriptions, this.descriptions) &&
       isSameData(other.photo, this.photo) &&
       isSameData(other.price, this.price) &&
       other.portionSize == this.portionSize &&
       other.portionUnit == this.portionUnit &&
       isSameList<Allergens>(other.allergens, this.allergens) &&
       isSameData(other.ingredients, this.ingredients) &&
       isSameData(other.nutrients, this.nutrients);
  }
}

export class MenuItemBuilder extends DataModelBuilder<MenuItem> {
  build(context: SerializationContext, map: SerializedData): MenuItem {
    return MenuItem.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    this.migrateEnum(map, 'portionUnit', PortionUnit, PortionUnit.Gram);

    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(MenuItem.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(MenuItem.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version the following parameters were introduced:
    //  - `portionSize` of type `number`
    //  - `portionUnit` of type `PortionUnit`
    //  - `allergens` of type `Allergens[]`
    //  - `ingredients` of type `LocalizedString | null`
    //  - `nutrients` of type `Nutrients | null`
    if(version < 2) {
      map['portionSize'] ??= 0.0;
      map['portionUnit'] ??= fromEnumAsString(PortionUnit.Gram);
      map['allergens'] ??= new Array<string>();

      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
